import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Flex,
  Box,
  Text,
  Heading,
  Divider,
  SimpleGrid,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useBreakpointValue,
} from '@chakra-ui/react';
import { WarningTwoIcon } from '@chakra-ui/icons';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import AssignmentCard from './components/AssignmentCard';
import EssayCard from './components/EssayCard';
import PromptAssignCard from './components/PromptAssignCard';

function StudentAssignmentPage() {
  // console.log('entering assignment page ......');
  const user = useSelector((state) => state.user?.info);
  // console.log('user info in student assignment page: ', user);
  const [essays, setEssays] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [promptId, setPromptId] = useState('');
  const [promptData, setPromptData] = useState({});
  const essayColumns = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchUserAssignments = async () => {
    try {
      const grade = user?.grade;
      if (!grade) return;
      const { data } = await api.get(
        `${API_BASE}/api/student-essay/assignments?level=${grade}`
      );
      // console.log('user assigments data: ', data);
      setEssays(data.data?.workingEssays);
      if (user?.isPlanActive) {
        setAssignments(data.data?.comingAssignments);
      }
    } catch (error) {
      console.error('Failed to fetch user assignments:', error);
    }
  };

  useEffect(() => {
    fetchUserAssignments();
  }, [user]);

  function handleSelectAssignment(data) {
    setPromptId(data?.promptId);
    setPromptData(data);
    onOpen();
  }

  const assignExercise = async () => {
    await api.get(`${API_BASE}/api/student-essay/new?promptId=${promptId}`);
    fetchUserAssignments();
    onClose();
  };

  const noWorkingAssignmentWarning = () => (
    <Box
      borderRadius="md"
      overflow="hidden"
      boxShadow="md"
      borderWidth="1px"
      textAlign="center"
      p={6}
      my={4}
      maxW="600px"
      alignSelf="center"
    >
      <WarningTwoIcon boxSize={'50px'} color={'orange.300'} />
      <Heading as="h2" size="md" mt={6} mb={2}>
        No Assignment yet!
      </Heading>
      <Text color={'gray.500'}>
        You don&apos;t have any assignments at the moment. Choose one from the{' '}
        <Text as="span" fontWeight="bold">
          Coming Soon
        </Text>{' '}
        section below to begin your writing journey!
      </Text>
    </Box>
  );

  const noComingAssignmentWarning = () => (
    <Box
      borderRadius="md"
      overflow="hidden"
      boxShadow="md"
      borderWidth="1px"
      textAlign="center"
      p={6}
      my={4}
      maxW="600px"
      alignSelf="center"
    >
      <WarningTwoIcon boxSize={'50px'} color={'orange.300'} />
      <Heading as="h2" size="md" mt={6} mb={2}>
        No Coming Assignment!
      </Heading>
      {!user?.grade && (
        <Text fontSize="md" color="gray.600">
          Oops! It seems your grade information is missing. Head over to the{' '}
          <Link color="blue.500" cursor="pointer" href="/settings">
            Profile
          </Link>{' '}
          page to complete your details and unlock access to all features!
        </Text>
      )}
      {!user?.isPlanActive && (
        <Text fontSize="md" color="gray.600">
          Your free trial or subscription has expired.{' '}
          <Link color="blue.500" cursor="pointer" href="/pricing">
            Renew
          </Link>{' '}
          now to continue enjoying full access to all features.
        </Text>
      )}
    </Box>
  );

  return (
    <Flex flexDirection="column">
      <Heading size="lg">Essay Adventure Awaits!</Heading>
      <Divider mt={4} mb={4} />
      <Heading size="md" mb={4} color="teal.700">
        Creative Zone: Essays in Progress
      </Heading>
      {!essays || essays?.length === 0 ? (
        noWorkingAssignmentWarning()
      ) : (
        <Flex flexWrap="wrap">
          <SimpleGrid
            w="100%"
            columns={essayColumns}
            gap={{ base: '8px', md: '20px' }}
            rowGap="48px"
          >
            {essays?.map((essay) => (
              <EssayCard
                key={essay.essayId}
                id={essay.essayId}
                title={essay.title}
                essayType={essay.essayType}
                status={essay.status}
                url={essay.url}
                dueDate={essay.dueDate}
              />
            ))}
          </SimpleGrid>
        </Flex>
      )}
      <Divider mt={4} mb={4} />
      <Heading size="md" mb={4} color="teal.700">
        Coming Soon: Next Challenges
      </Heading>
      {!assignments || assignments.length === 0 ? (
        noComingAssignmentWarning()
      ) : (
        <Flex flexWrap="wrap">
          <SimpleGrid
            w="100%"
            columns={essayColumns}
            gap={{ base: '8px', md: '20px' }}
            rowGap="48px"
          >
            {assignments?.map((prompt) => (
              <AssignmentCard
                key={prompt.promptId}
                prompt={prompt}
                // id={prompt.promptId}
                // title={prompt.title}
                // essayType={prompt.primaryType}
                // url={prompt.thumbnailUrl}
                selectAssignment={handleSelectAssignment}
              />
            ))}
          </SimpleGrid>
        </Flex>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Writing Prompt</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <PromptAssignCard promptData={promptData} />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="green"
              mr={4}
              isDisabled={!user?.isPlanActive}
              onClick={() => assignExercise()}
            >
              Take
            </Button>
            <Button colorScheme="green" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default StudentAssignmentPage;
